import { useCallback, useEffect, useRef, useState } from "react";
import { API, api } from "@/lib/api";

const KEY = "vinsafe-advisor-session";
const newId = () => (crypto.randomUUID ? crypto.randomUUID() : "10000000-1000-4000-8000-100000000000".replace(/[018]/g, (c) => (c ^ (Math.random() * 16) >> (c / 4)).toString(16)));

const readError = async (res) => {
  try {
    const j = await res.json();
    return typeof j.detail === "string" ? j.detail : "Something went wrong. Please try again.";
  } catch {
    return "Something went wrong. Please try again.";
  }
};

export function useAdvisor() {
  const [sessionId, setSessionId] = useState(() => localStorage.getItem(KEY) || newId());
  const [messages, setMessages] = useState([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const loaded = useRef(false);
  const current = useRef(sessionId);

  useEffect(() => {
    current.current = sessionId;
    localStorage.setItem(KEY, sessionId);
  }, [sessionId]);

  const load = useCallback(async () => {
    if (loaded.current) return;
    loaded.current = true;
    try {
      const { data } = await api.get(`/advisor/sessions/${sessionId}`);
      if (current.current !== sessionId) return;
      setMessages(data.messages.map(({ role, content }) => ({ role, content })));
    } catch {
      setMessages([]);
    }
  }, [sessionId]);

  const reset = () => {
    setMessages([]);
    setError("");
    setSessionId(newId());
  };

  const append = (t) => setMessages((m) => [...m.slice(0, -1), { role: "assistant", content: m[m.length - 1].content + t }]);

  const send = async (text) => {
    if (!text.trim() || busy) return;
    setError("");
    setBusy(true);
    setMessages((m) => [...m, { role: "user", content: text.trim() }, { role: "assistant", content: "" }]);
    try {
      const res = await fetch(`${API}/advisor/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ session_id: sessionId, message: text.trim(), page: window.location.pathname }),
      });
      if (!res.ok) throw new Error(await readError(res));
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buf = "";
      for (;;) {
        const { value, done } = await reader.read();
        if (done) break;
        buf += decoder.decode(value, { stream: true });
        const parts = buf.split("\n\n");
        buf = parts.pop();
        parts.forEach((p) => {
          const line = p.replace(/^data: /, "");
          if (!line) return;
          const ev = JSON.parse(line);
          if (ev.t) append(ev.t);
          if (ev.error) setError(ev.error);
        });
      }
    } catch (e) {
      setError(e.message);
      setMessages((m) => (m[m.length - 1]?.content ? m : m.slice(0, -1)));
    } finally {
      setBusy(false);
    }
  };

  return { messages, busy, error, send, reset, load };
}
